import React, { useEffect, useState, useRef, createElement } from 'react';
import { CheckCircle2, Shield, X, TrendingUp, Clock, DollarSign, Target, Calendar, Award, Users, Briefcase } from 'lucide-react';
export function SalesPage() {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  useEffect(() => {
    // Set page title
    document.title = 'D&A TIMEPIECES — Luxury Watch Business From Scratch';
    // Update or create Open Graph meta tags
    const updateMetaTag = (property: string, content: string) => {
      let metaTag = document.querySelector(`meta[property='${property}']`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };
    // Set Open Graph title
    updateMetaTag('og:title', 'D&A TIMEPIECES — Luxury Watch Business From Scratch');
    updateMetaTag('og:site_name', 'D&A TIMEPIECES');
    // Set standard meta tags
    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement('meta');
      descriptionTag.setAttribute('name', 'description');
      document.head.appendChild(descriptionTag);
    }
    descriptionTag.setAttribute('content', 'Turn luxury watches into daily income with zero inventory.');
    // Meta Pixel Code - Safe implementation for React
    if (!window.fbq) {
      // Initialize fbq function
      const fbq: any = function () {
        fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
      };
      if (!window._fbq) window._fbq = fbq;
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = '2.0';
      fbq.queue = [];
      window.fbq = fbq;
      // Create and append script
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
      // Initialize pixel after script loads
      script.onload = () => {
        window.fbq('init', '1523508032023471');
        window.fbq('track', 'PageView');
      };
    } else {
      // If fbq already exists, just track pageview
      window.fbq('track', 'PageView');
    }
    // Add noscript fallback
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = 'https://www.facebook.com/tr?id=1523508032023471&ev=PageView&noscript=1';
    noscript.appendChild(img);
    document.body.appendChild(noscript);
    // Sticky CTA bar scroll handler
    const handleScroll = () => {
      if (window.scrollY > 800) {
        setShowStickyBar(true);
      } else {
        setShowStickyBar(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    observerRef.current = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-visible');
        }
      });
    }, {
      threshold: 0.1
    });
    document.querySelectorAll('.fade-in').forEach(el => {
      observerRef.current?.observe(el);
    });
    return () => {
      observerRef.current?.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  return <div className="min-h-screen bg-[#000000] text-white" style={{
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", Inter, system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  }} data-id="element-2">
      {/* Sticky Mobile CTA Bar */}
      <div className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transition-transform duration-300 ${showStickyBar ? 'translate-y-0' : 'translate-y-full'}`} style={{
      background: 'linear-gradient(to top, #000000 80%, transparent)',
      backdropFilter: 'blur(10px)'
    }} data-id="element-3">
        <div className="p-4" data-id="element-4">
          <a href="https://pay.hotmart.com/R103200621U" target="_blank" rel="noopener noreferrer" className="block w-full text-center px-8 py-4 bg-[#D4AF37] text-black font-bold text-base rounded-full transition-all duration-300" data-id="element-5">
            Get Instant Access – $17.99
          </a>
        </div>
      </div>

      {/* SECTION 1 — HERO */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-24 bg-[#000000]" data-id="element-6">
        <div className="max-w-7xl mx-auto w-full" data-id="element-7">
          <div className="grid lg:grid-cols-2 gap-20 items-center" data-id="element-8">
            <div className="fade-in space-y-8" data-id="element-9">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] text-white" data-id="element-10">
                How to Start Selling{' '}
                <span className="text-[#D4AF37]" data-id="element-11">Luxury Watches</span> Without
                Inventory or Costly Beginner Mistakes
              </h1>

              <p className="text-lg text-[#999999] leading-relaxed max-w-xl" data-id="element-12">
                A clear step-by-step system based on real experience inside the
                luxury watch market.
              </p>

              <ul className="space-y-3 text-base text-[#cccccc]" data-id="element-13">
                <li className="flex items-start gap-3" data-id="element-14">
                  <span className="text-[#D4AF37] mt-1" data-id="element-15">•</span>
                  <span data-id="element-16">Learn how watches are actually priced</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-17">
                  <span className="text-[#D4AF37] mt-1" data-id="element-18">•</span>
                  <span data-id="element-19">Avoid fake sellers and bad deals</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-20">
                  <span className="text-[#D4AF37] mt-1" data-id="element-21">•</span>
                  <span data-id="element-22">Understand what models really sell</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-23">
                  <span className="text-[#D4AF37] mt-1" data-id="element-24">•</span>
                  <span data-id="element-25">Start with confidence instead of guessing</span>
                </li>
              </ul>

              <div className="pt-4" data-id="element-26">
                <a href="https://pay.hotmart.com/R103200621U" target="_blank" rel="noopener noreferrer" className="inline-block px-10 py-4 bg-[#D4AF37] text-black font-semibold text-base rounded-full hover:bg-[#C4A137] transition-all duration-200" data-id="element-27">
                  Get Instant Access – $17.99
                </a>
                <p className="text-sm text-[#666666] mt-4" data-id="element-28">
                  Instant Access • Beginner-Friendly • 30-Day Guarantee
                </p>
              </div>
            </div>

            <div className="fade-in flex justify-center lg:justify-end" data-id="element-29">
              <div className="relative max-w-xl w-full" data-id="element-30">
                <img src="https://cdn.magicpatterns.com/uploads/3L4vsPD2ZqNWT1EkaVgNM6/post_pepa_si.png" alt="Luxury Watch Business System" className="w-full h-auto" data-id="element-31" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — WHY YOU'RE STUCK */}
      <section className="fade-in py-24 px-6 bg-[#000000] border-t border-[#1a1a1a]" data-id="element-32">
        <div className="max-w-5xl mx-auto" data-id="element-33">
          <div className="text-center mb-16" data-id="element-34">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-35">
              Why Most People{' '}
              <span className="text-[#D4AF37]" data-id="element-36">Never Succeed</span> in the Watch
              Business
            </h2>
            <p className="text-lg text-[#999999] max-w-3xl mx-auto" data-id="element-37">
              It's not your fault. The watch industry is intentionally confusing
              for beginners.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5" data-id="element-38">
            <div className="flex items-start gap-4 bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-39">
              <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-40" />
              <div data-id="element-41">
                <h3 className="text-base font-semibold text-white mb-2" data-id="element-42">
                  No Clear Roadmap
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-43">
                  YouTube videos give you pieces, but never the full system
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-44">
              <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-45" />
              <div data-id="element-46">
                <h3 className="text-base font-semibold text-white mb-2" data-id="element-47">
                  Fear of Fakes
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-48">
                  You're terrified of buying a counterfeit and losing money
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-49">
              <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-50" />
              <div data-id="element-51">
                <h3 className="text-base font-semibold text-white mb-2" data-id="element-52">
                  Don't Know Where to Source
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-53">
                  You have no idea where dealers find profitable watches
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-54">
              <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-55" />
              <div data-id="element-56">
                <h3 className="text-base font-semibold text-white mb-2" data-id="element-57">
                  Weak Negotiation Skills
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-58">
                  You overpay because you don't know how to negotiate deals
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-59">
              <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-60" />
              <div data-id="element-61">
                <h3 className="text-base font-semibold text-white mb-2" data-id="element-62">
                  No Sales System
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-63">
                  You don't know how to close deals or follow up with buyers
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-64">
              <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-65" />
              <div data-id="element-66">
                <h3 className="text-base font-semibold text-white mb-2" data-id="element-67">
                  Think You Need Thousands to Start
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-68">
                  You believe you need massive capital to flip watches
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — BEFORE VS AFTER */}
      <section className="fade-in py-24 px-6 bg-[#000000] border-t border-[#1a1a1a]" data-id="element-69">
        <div className="max-w-6xl mx-auto" data-id="element-70">
          <div className="text-center mb-16" data-id="element-71">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-72">
              The <span className="text-[#D4AF37]" data-id="element-73">Transformation</span> You'll
              Experience
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-74">
            <div className="bg-[#0a0a0a] p-8 rounded-xl border border-[#1a1a1a]" data-id="element-75">
              <div className="text-center mb-6" data-id="element-76">
                <div className="inline-block px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-full mb-4" data-id="element-77">
                  <span className="text-red-500 font-semibold text-sm" data-id="element-78">
                    BEFORE
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-white" data-id="element-79">
                  Stuck & Confused
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-80">
                <li className="flex items-start gap-3" data-id="element-81">
                  <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-82" />
                  <span className="text-sm text-[#999999]" data-id="element-83">
                    Watching YouTube videos but never taking action
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-84">
                  <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-85" />
                  <span className="text-sm text-[#999999]" data-id="element-86">
                    Afraid of buying fakes or getting scammed
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-87">
                  <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-88" />
                  <span className="text-sm text-[#999999]" data-id="element-89">
                    No idea where to find profitable deals
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-90">
                  <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-91" />
                  <span className="text-sm text-[#999999]" data-id="element-92">
                    Stuck in your 9-5 with no side income
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-93">
                  <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-94" />
                  <span className="text-sm text-[#999999]" data-id="element-95">
                    Wasting time on trial and error
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-[#0a0a0a] p-8 rounded-xl border border-[#D4AF37]" data-id="element-96">
              <div className="text-center mb-6" data-id="element-97">
                <div className="inline-block px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full mb-4" data-id="element-98">
                  <span className="text-[#D4AF37] font-semibold text-sm" data-id="element-99">
                    AFTER
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-white" data-id="element-100">
                  Confident & Profitable
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-101">
                <li className="flex items-start gap-3" data-id="element-102">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" data-id="element-103" />
                  <span className="text-sm text-[#cccccc]" data-id="element-104">
                    Following a proven step-by-step system
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-105">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" data-id="element-106" />
                  <span className="text-sm text-[#cccccc]" data-id="element-107">
                    Confidently verifying authenticity like a pro
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-108">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" data-id="element-109" />
                  <span className="text-sm text-[#cccccc]" data-id="element-110">
                    Sourcing undervalued watches others miss
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-111">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" data-id="element-112" />
                  <span className="text-sm text-[#cccccc]" data-id="element-113">
                    Making your first $500-$1,000+ flips
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-114">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" data-id="element-115" />
                  <span className="text-sm text-[#cccccc]" data-id="element-116">
                    Building a real income stream you control
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — WHAT YOU GET */}
      <section className="fade-in py-24 px-6 bg-[#000000] border-t border-[#1a1a1a]" data-id="element-117">
        <div className="max-w-6xl mx-auto" data-id="element-118">
          <div className="text-center mb-16" data-id="element-119">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-120">
              Everything You Get Inside the{' '}
              <span className="text-[#D4AF37]" data-id="element-121">
                Luxury Watch Business System
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-122">
            <div className="bg-[#0a0a0a] p-8 rounded-xl border border-[#1a1a1a]" data-id="element-123">
              <div className="flex items-start gap-5" data-id="element-124">
                <div className="text-4xl" data-id="element-125">📘</div>
                <div data-id="element-126">
                  <h3 className="text-xl font-semibold mb-2 text-white" data-id="element-127">
                    Luxury Watch Business From Scratch (Step-by-Step System)
                  </h3>
                  <p className="text-[#999999] text-sm leading-relaxed" data-id="element-128">
                    Complete foundational guide for beginners.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-8 rounded-xl border border-[#1a1a1a]" data-id="element-129">
              <div className="flex items-start gap-5" data-id="element-130">
                <div className="text-4xl" data-id="element-131">🛠️</div>
                <div data-id="element-132">
                  <h3 className="text-xl font-semibold mb-2 text-white" data-id="element-133">
                    Negotiation & Pricing Templates
                  </h3>
                  <p className="text-[#999999] text-sm leading-relaxed" data-id="element-134">
                    Plug-and-play templates to use instantly.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-8 rounded-xl border border-[#1a1a1a]" data-id="element-135">
              <div className="flex items-start gap-5" data-id="element-136">
                <div className="text-4xl" data-id="element-137">🔍</div>
                <div data-id="element-138">
                  <h3 className="text-xl font-semibold mb-2 text-white" data-id="element-139">
                    Watch Sourcing Guide
                  </h3>
                  <p className="text-[#999999] text-sm leading-relaxed" data-id="element-140">
                    Learn where to find profitable deals others miss.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-8 rounded-xl border border-[#1a1a1a]" data-id="element-141">
              <div className="flex items-start gap-5" data-id="element-142">
                <div className="text-4xl" data-id="element-143">🧠</div>
                <div data-id="element-144">
                  <h3 className="text-xl font-semibold mb-2 text-white" data-id="element-145">
                    Bonus Lessons: Buy Low, Sell High
                  </h3>
                  <p className="text-[#999999] text-sm leading-relaxed" data-id="element-146">
                    Strategies for maximizing profit safely.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-8 rounded-xl border border-[#1a1a1a] md:col-span-2" data-id="element-147">
              <div className="flex items-start gap-5" data-id="element-148">
                <div className="text-4xl" data-id="element-149">📱</div>
                <div data-id="element-150">
                  <h3 className="text-xl font-semibold mb-2 text-white" data-id="element-151">
                    Sales Scripts (DM, Phone, Follow-Up)
                  </h3>
                  <p className="text-[#999999] text-sm leading-relaxed" data-id="element-152">
                    Copy-paste scripts to close more deals.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center" data-id="element-153">
            <a href="https://pay.hotmart.com/R103200621U" target="_blank" rel="noopener noreferrer" className="inline-block px-10 py-4 bg-[#D4AF37] text-black font-semibold text-base rounded-full hover:bg-[#C4A137] transition-all duration-200" data-id="element-154">
              Get Instant Access – $17.99
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 5 — WHAT YOU'LL LEARN INSIDE */}
      <section className="fade-in py-24 px-6 bg-[#000000] border-t border-[#1a1a1a]" data-id="element-155">
        <div className="max-w-5xl mx-auto" data-id="element-156">
          <div className="text-center mb-16" data-id="element-157">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-158">
              What You'll Learn <span className="text-[#D4AF37]" data-id="element-159">Inside</span>
            </h2>
            <p className="text-lg text-[#999999]" data-id="element-160">
              A complete, step-by-step curriculum designed for beginners
            </p>
          </div>

          <div className="space-y-5" data-id="element-161">
            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-162">
              <div className="flex items-start gap-4" data-id="element-163">
                <div className="flex-shrink-0 w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center" data-id="element-164">
                  <span className="text-black font-bold text-sm" data-id="element-165">1</span>
                </div>
                <div data-id="element-166">
                  <h3 className="text-lg font-semibold text-white mb-2" data-id="element-167">
                    Watch Business Fundamentals
                  </h3>
                  <p className="text-sm text-[#999999]" data-id="element-168">
                    Understanding the market, key brands, and how dealers make
                    money
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-169">
              <div className="flex items-start gap-4" data-id="element-170">
                <div className="flex-shrink-0 w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center" data-id="element-171">
                  <span className="text-black font-bold text-sm" data-id="element-172">2</span>
                </div>
                <div data-id="element-173">
                  <h3 className="text-lg font-semibold text-white mb-2" data-id="element-174">
                    How to Verify Authenticity
                  </h3>
                  <p className="text-sm text-[#999999]" data-id="element-175">
                    Step-by-step authentication process to avoid fakes and scams
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-176">
              <div className="flex items-start gap-4" data-id="element-177">
                <div className="flex-shrink-0 w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center" data-id="element-178">
                  <span className="text-black font-bold text-sm" data-id="element-179">3</span>
                </div>
                <div data-id="element-180">
                  <h3 className="text-lg font-semibold text-white mb-2" data-id="element-181">
                    Where to Source Profitable Watches
                  </h3>
                  <p className="text-sm text-[#999999]" data-id="element-182">
                    The exact platforms and strategies dealers use to find
                    undervalued pieces
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-183">
              <div className="flex items-start gap-4" data-id="element-184">
                <div className="flex-shrink-0 w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center" data-id="element-185">
                  <span className="text-black font-bold text-sm" data-id="element-186">4</span>
                </div>
                <div data-id="element-187">
                  <h3 className="text-lg font-semibold text-white mb-2" data-id="element-188">
                    Negotiation & Pricing Strategies
                  </h3>
                  <p className="text-sm text-[#999999]" data-id="element-189">
                    How to negotiate like a pro and price watches for maximum
                    profit
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-190">
              <div className="flex items-start gap-4" data-id="element-191">
                <div className="flex-shrink-0 w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center" data-id="element-192">
                  <span className="text-black font-bold text-sm" data-id="element-193">5</span>
                </div>
                <div data-id="element-194">
                  <h3 className="text-lg font-semibold text-white mb-2" data-id="element-195">
                    Sales & Marketing Systems
                  </h3>
                  <p className="text-sm text-[#999999]" data-id="element-196">
                    Copy-paste scripts, follow-up sequences, and closing
                    techniques that work
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-197">
              <div className="flex items-start gap-4" data-id="element-198">
                <div className="flex-shrink-0 w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center" data-id="element-199">
                  <span className="text-black font-bold text-sm" data-id="element-200">6</span>
                </div>
                <div data-id="element-201">
                  <h3 className="text-lg font-semibold text-white mb-2" data-id="element-202">
                    Scaling Your Watch Business
                  </h3>
                  <p className="text-sm text-[#999999]" data-id="element-203">
                    How to go from your first flip to consistent monthly income
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — 7-DAY ACTION PLAN */}
      <section className="fade-in py-24 px-6 bg-[#000000] border-t border-[#1a1a1a]" data-id="element-204">
        <div className="max-w-5xl mx-auto" data-id="element-205">
          <div className="text-center mb-16" data-id="element-206">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-207">
              Your First <span className="text-[#D4AF37]" data-id="element-208">7 Days</span> Inside
              the Watch Business
            </h2>
            <p className="text-lg text-[#999999]" data-id="element-209">
              Follow this simple roadmap to make your first flip
            </p>
          </div>

          <div className="space-y-4" data-id="element-210">
            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-start gap-5" data-id="element-211">
              <div className="flex-shrink-0" data-id="element-212">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center" data-id="element-213">
                  <Calendar className="w-6 h-6 text-black" data-id="element-214" />
                </div>
              </div>
              <div data-id="element-215">
                <h3 className="text-lg font-semibold text-white mb-2" data-id="element-216">
                  Day 1 — Understand the Market
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-217">
                  Learn watch business fundamentals, key brands, and how the
                  market works
                </p>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-start gap-5" data-id="element-218">
              <div className="flex-shrink-0" data-id="element-219">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center" data-id="element-220">
                  <Shield className="w-6 h-6 text-black" data-id="element-221" />
                </div>
              </div>
              <div data-id="element-222">
                <h3 className="text-lg font-semibold text-white mb-2" data-id="element-223">
                  Day 2 — Learn Basic Authentication
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-224">
                  Master the verification process to confidently spot fakes and
                  avoid scams
                </p>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-start gap-5" data-id="element-225">
              <div className="flex-shrink-0" data-id="element-226">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center" data-id="element-227">
                  <Target className="w-6 h-6 text-black" data-id="element-228" />
                </div>
              </div>
              <div data-id="element-229">
                <h3 className="text-lg font-semibold text-white mb-2" data-id="element-230">
                  Day 3 — Find Undervalued Listings
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-231">
                  Use the sourcing guide to discover profitable watches others
                  are missing
                </p>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-start gap-5" data-id="element-232">
              <div className="flex-shrink-0" data-id="element-233">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center" data-id="element-234">
                  <DollarSign className="w-6 h-6 text-black" data-id="element-235" />
                </div>
              </div>
              <div data-id="element-236">
                <h3 className="text-lg font-semibold text-white mb-2" data-id="element-237">
                  Day 4 — Make Your First Offers
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-238">
                  Use the negotiation scripts to confidently reach out and
                  negotiate deals
                </p>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-start gap-5" data-id="element-239">
              <div className="flex-shrink-0" data-id="element-240">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center" data-id="element-241">
                  <Users className="w-6 h-6 text-black" data-id="element-242" />
                </div>
              </div>
              <div data-id="element-243">
                <h3 className="text-lg font-semibold text-white mb-2" data-id="element-244">
                  Day 5 — Follow Up & Handle Objections
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-245">
                  Use proven follow-up sequences to overcome objections and move
                  deals forward
                </p>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-start gap-5" data-id="element-246">
              <div className="flex-shrink-0" data-id="element-247">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center" data-id="element-248">
                  <CheckCircle2 className="w-6 h-6 text-black" data-id="element-249" />
                </div>
              </div>
              <div data-id="element-250">
                <h3 className="text-lg font-semibold text-white mb-2" data-id="element-251">
                  Day 6 — Close Your First Deal
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-252">
                  Complete your first transaction safely and profitably using
                  the system
                </p>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#D4AF37] flex items-start gap-5" data-id="element-253">
              <div className="flex-shrink-0" data-id="element-254">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center" data-id="element-255">
                  <TrendingUp className="w-6 h-6 text-black" data-id="element-256" />
                </div>
              </div>
              <div data-id="element-257">
                <h3 className="text-lg font-semibold text-white mb-2" data-id="element-258">
                  Day 7 — Repeat & Scale
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-259">
                  Apply the same process to build consistent income and scale
                  your business
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7 — WHY THIS WORKS */}
      <section className="fade-in py-24 px-6 bg-[#000000] border-t border-[#1a1a1a]" data-id="element-260">
        <div className="max-w-5xl mx-auto" data-id="element-261">
          <div className="text-center mb-16" data-id="element-262">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-263">
              Why This System Works —{' '}
              <span className="text-[#D4AF37]" data-id="element-264">
                Even If You're a Total Beginner
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5" data-id="element-265">
            <div className="flex items-start gap-4 bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-266">
              <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" data-id="element-267" />
              <p className="text-base text-[#cccccc]" data-id="element-268">
                No startup capital required
              </p>
            </div>

            <div className="flex items-start gap-4 bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-269">
              <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" data-id="element-270" />
              <p className="text-base text-[#cccccc]" data-id="element-271">
                Learn to verify authenticity and avoid fakes
              </p>
            </div>

            <div className="flex items-start gap-4 bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-272">
              <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" data-id="element-273" />
              <p className="text-base text-[#cccccc]" data-id="element-274">
                Strategies for sourcing undervalued watches
              </p>
            </div>

            <div className="flex items-start gap-4 bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a]" data-id="element-275">
              <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" data-id="element-276" />
              <p className="text-base text-[#cccccc]" data-id="element-277">
                Avoid expensive mistakes beginners make
              </p>
            </div>

            <div className="flex items-start gap-4 bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] md:col-span-2" data-id="element-278">
              <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" data-id="element-279" />
              <p className="text-base text-[#cccccc]" data-id="element-280">
                Scripts that work even if you've never sold before
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8 — TESTIMONIALS (ENHANCED LUXURY CARDS) */}
      <section className="fade-in py-24 px-6 bg-[#000000] border-t border-[#1a1a1a]" data-id="element-281">
        <div className="max-w-6xl mx-auto" data-id="element-282">
          <div className="text-center mb-16" data-id="element-283">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-284">
              Real Results From{' '}
              <span className="text-[#D4AF37]" data-id="element-285">Beginners</span> Like You
            </h2>
            <p className="text-lg text-[#999999]" data-id="element-286">
              See what students are achieving with this system
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-287">
            {/* Testimonial 1 - LUXURY ENHANCED */}
            <div className="bg-[#0D0D0D] p-10 rounded-2xl" style={{
            border: '1.5px solid #D4AF37',
            boxShadow: '0 0 30px rgba(212, 175, 55, 0.15), 0 20px 60px rgba(0, 0, 0, 0.4)'
          }} data-id="element-288">
              <div className="flex flex-col items-center text-center" data-id="element-289">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=faces&q=80" alt="Kevin R." className="w-20 h-20 rounded-full object-cover mb-4 ring-2 ring-[#D4AF37]" data-id="element-290" />
                <p className="font-semibold text-lg text-white mb-1" data-id="element-291">
                  Kevin R.
                </p>
                <p className="text-sm text-[#999999] mb-3" data-id="element-292">
                  First-Time Flipper
                </p>
                <div className="flex gap-0.5 mb-4" data-id="element-293">
                  {[...Array(5)].map((_, i) => <span key={i} className="text-[#D4AF37] text-base" data-id="element-294">
                      ★
                    </span>)}
                </div>
                <p className="text-[#cccccc] text-sm leading-relaxed" data-id="element-295">
                  "I flipped my first Seiko and made{' '}
                  <strong className="text-white" data-id="element-296">$120 profit</strong>. I knew
                  NOTHING before this guide."
                </p>
              </div>
            </div>

            {/* Testimonial 2 - LUXURY ENHANCED */}
            <div className="bg-[#0D0D0D] p-10 rounded-2xl" style={{
            border: '1.5px solid #D4AF37',
            boxShadow: '0 0 30px rgba(212, 175, 55, 0.15), 0 20px 60px rgba(0, 0, 0, 0.4)'
          }} data-id="element-297">
              <div className="flex flex-col items-center text-center" data-id="element-298">
                <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&crop=faces&q=80" alt="Miguel A." className="w-20 h-20 rounded-full object-cover mb-4 ring-2 ring-[#D4AF37]" data-id="element-299" />
                <p className="font-semibold text-lg text-white mb-1" data-id="element-300">
                  Miguel A.
                </p>
                <p className="text-sm text-[#999999] mb-3" data-id="element-301">
                  Side Hustle Builder
                </p>
                <div className="flex gap-0.5 mb-4" data-id="element-302">
                  {[...Array(5)].map((_, i) => <span key={i} className="text-[#D4AF37] text-base" data-id="element-303">
                      ★
                    </span>)}
                </div>
                <p className="text-[#cccccc] text-sm leading-relaxed" data-id="element-304">
                  "The sourcing guide alone is worth{' '}
                  <strong className="text-white" data-id="element-305">$200</strong>. This saved me
                  months of trial and error."
                </p>
              </div>
            </div>

            {/* Testimonial 3 - LUXURY ENHANCED */}
            <div className="bg-[#0D0D0D] p-10 rounded-2xl" style={{
            border: '1.5px solid #D4AF37',
            boxShadow: '0 0 30px rgba(212, 175, 55, 0.15), 0 20px 60px rgba(0, 0, 0, 0.4)'
          }} data-id="element-306">
              <div className="flex flex-col items-center text-center" data-id="element-307">
                <img src="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200&h=200&fit=crop&crop=faces&q=80" alt="Daniel P." className="w-20 h-20 rounded-full object-cover mb-4 ring-2 ring-[#D4AF37]" data-id="element-308" />
                <p className="font-semibold text-lg text-white mb-1" data-id="element-309">
                  Daniel P.
                </p>
                <p className="text-sm text-[#999999] mb-3" data-id="element-310">Complete Beginner</p>
                <div className="flex gap-0.5 mb-4" data-id="element-311">
                  {[...Array(5)].map((_, i) => <span key={i} className="text-[#D4AF37] text-base" data-id="element-312">
                      ★
                    </span>)}
                </div>
                <p className="text-[#cccccc] text-sm leading-relaxed" data-id="element-313">
                  "I was terrified of buying a fake watch. The{' '}
                  <strong className="text-white" data-id="element-314">verification steps</strong>{' '}
                  made everything simple."
                </p>
              </div>
            </div>

            {/* Testimonial 4 - LUXURY ENHANCED */}
            <div className="bg-[#0D0D0D] p-10 rounded-2xl" style={{
            border: '1.5px solid #D4AF37',
            boxShadow: '0 0 30px rgba(212, 175, 55, 0.15), 0 20px 60px rgba(0, 0, 0, 0.4)'
          }} data-id="element-315">
              <div className="flex flex-col items-center text-center" data-id="element-316">
                <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=faces&q=80" alt="James T." className="w-20 h-20 rounded-full object-cover mb-4 ring-2 ring-[#D4AF37]" data-id="element-317" />
                <p className="font-semibold text-lg text-white mb-1" data-id="element-318">
                  James T.
                </p>
                <p className="text-sm text-[#999999] mb-3" data-id="element-319">Part-Time Dealer</p>
                <div className="flex gap-0.5 mb-4" data-id="element-320">
                  {[...Array(5)].map((_, i) => <span key={i} className="text-[#D4AF37] text-base" data-id="element-321">
                      ★
                    </span>)}
                </div>
                <p className="text-[#cccccc] text-sm leading-relaxed" data-id="element-322">
                  "Made{' '}
                  <strong className="text-white" data-id="element-323">$850 on my third flip</strong>.
                  The negotiation templates are pure gold."
                </p>
              </div>
            </div>

            {/* Testimonial 5 - LUXURY ENHANCED */}
            <div className="bg-[#0D0D0D] p-10 rounded-2xl" style={{
            border: '1.5px solid #D4AF37',
            boxShadow: '0 0 30px rgba(212, 175, 55, 0.15), 0 20px 60px rgba(0, 0, 0, 0.4)'
          }} data-id="element-324">
              <div className="flex flex-col items-center text-center" data-id="element-325">
                <img src="https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=200&h=200&fit=crop&crop=faces&q=80" alt="Alex M." className="w-20 h-20 rounded-full object-cover mb-4 ring-2 ring-[#D4AF37]" data-id="element-326" />
                <p className="font-semibold text-lg text-white mb-1" data-id="element-327">Alex M.</p>
                <p className="text-sm text-[#999999] mb-3" data-id="element-328">Watch Enthusiast</p>
                <div className="flex gap-0.5 mb-4" data-id="element-329">
                  {[...Array(5)].map((_, i) => <span key={i} className="text-[#D4AF37] text-base" data-id="element-330">
                      ★
                    </span>)}
                </div>
                <p className="text-[#cccccc] text-sm leading-relaxed" data-id="element-331">
                  "Finally understand the market. Went from{' '}
                  <strong className="text-white" data-id="element-332">confused to confident</strong>{' '}
                  in one week."
                </p>
              </div>
            </div>

            {/* Testimonial 6 - LUXURY ENHANCED */}
            <div className="bg-[#0D0D0D] p-10 rounded-2xl" style={{
            border: '1.5px solid #D4AF37',
            boxShadow: '0 0 30px rgba(212, 175, 55, 0.15), 0 20px 60px rgba(0, 0, 0, 0.4)'
          }} data-id="element-333">
              <div className="flex flex-col items-center text-center" data-id="element-334">
                <img src="https://images.unsplash.com/photo-1463453091185-61582044d556?w=200&h=200&fit=crop&crop=faces&q=80" alt="Ryan K." className="w-20 h-20 rounded-full object-cover mb-4 ring-2 ring-[#D4AF37]" data-id="element-335" />
                <p className="font-semibold text-lg text-white mb-1" data-id="element-336">Ryan K.</p>
                <p className="text-sm text-[#999999] mb-3" data-id="element-337">New Entrepreneur</p>
                <div className="flex gap-0.5 mb-4" data-id="element-338">
                  {[...Array(5)].map((_, i) => <span key={i} className="text-[#D4AF37] text-base" data-id="element-339">
                      ★
                    </span>)}
                </div>
                <p className="text-[#cccccc] text-sm leading-relaxed" data-id="element-340">
                  "The <strong className="text-white" data-id="element-341">sales scripts</strong>{' '}
                  work perfectly. Closed 3 deals in my first month."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 9 — AUTHORITY */}
      <section className="fade-in py-24 px-6 bg-[#000000] border-t border-[#1a1a1a]" data-id="element-342">
        <div className="max-w-5xl mx-auto" data-id="element-343">
          <div className="text-center mb-12" data-id="element-344">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-345">
              Why Learn <span className="text-[#D4AF37]" data-id="element-346">From Me?</span>
            </h2>
            <p className="text-lg text-[#999999] max-w-3xl mx-auto leading-relaxed" data-id="element-347">
              I've spent years in the luxury watch industry, buying and selling
              high-end timepieces. I've made every mistake so you don't have to.
              This system is built from real-world experience, not theory.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-348">
            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-start gap-4" data-id="element-349">
              <Award className="w-8 h-8 text-[#D4AF37] flex-shrink-0" data-id="element-350" />
              <div data-id="element-351">
                <h3 className="text-base font-semibold text-white mb-2" data-id="element-352">
                  Real Industry Experience
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-353">
                  Years of hands-on experience buying, selling, and
                  authenticating luxury watches
                </p>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-start gap-4" data-id="element-354">
              <TrendingUp className="w-8 h-8 text-[#D4AF37] flex-shrink-0" data-id="element-355" />
              <div data-id="element-356">
                <h3 className="text-base font-semibold text-white mb-2" data-id="element-357">
                  Proven Track Record
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-358">
                  Hundreds of successful deals closed across Rolex, Omega,
                  Cartier, and more
                </p>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-start gap-4" data-id="element-359">
              <Shield className="w-8 h-8 text-[#D4AF37] flex-shrink-0" data-id="element-360" />
              <div data-id="element-361">
                <h3 className="text-base font-semibold text-white mb-2" data-id="element-362">
                  Authentication Expertise
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-363">
                  Specialized training in spotting fakes and verifying
                  authenticity
                </p>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-start gap-4" data-id="element-364">
              <Briefcase className="w-8 h-8 text-[#D4AF37] flex-shrink-0" data-id="element-365" />
              <div data-id="element-366">
                <h3 className="text-base font-semibold text-white mb-2" data-id="element-367">
                  Negotiation Mastery
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-368">
                  Expert-level negotiation skills developed through real
                  transactions
                </p>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-start gap-4" data-id="element-369">
              <Target className="w-8 h-8 text-[#D4AF37] flex-shrink-0" data-id="element-370" />
              <div data-id="element-371">
                <h3 className="text-base font-semibold text-white mb-2" data-id="element-372">
                  Practical, Real-World System
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-373">
                  No fluff — only strategies that work in today's market
                </p>
              </div>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-start gap-4" data-id="element-374">
              <Users className="w-8 h-8 text-[#D4AF37] flex-shrink-0" data-id="element-375" />
              <div data-id="element-376">
                <h3 className="text-base font-semibold text-white mb-2" data-id="element-377">
                  Beginner-Focused Approach
                </h3>
                <p className="text-sm text-[#999999]" data-id="element-378">
                  Designed specifically for people with zero experience
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 10 — 30-DAY GUARANTEE */}
      <section className="fade-in py-24 px-6 bg-[#000000] border-t border-[#1a1a1a]" data-id="element-379">
        <div className="max-w-4xl mx-auto text-center" data-id="element-380">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-[#D4AF37] rounded-full mb-8" data-id="element-381">
            <Shield className="w-12 h-12 text-black" data-id="element-382" />
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-383">
            <span className="text-[#D4AF37]" data-id="element-384">30-Day</span> Money-Back Guarantee
          </h2>

          <p className="text-lg text-[#999999] leading-relaxed max-w-2xl mx-auto mb-8" data-id="element-385">
            If you don't feel confident flipping your first watch after
            completing the guide, message us and we'll refund{' '}
            <strong className="text-white" data-id="element-386">100% of your purchase</strong> — no
            questions asked.
          </p>

          <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] max-w-2xl mx-auto" data-id="element-387">
            <p className="text-sm text-[#cccccc]" data-id="element-388">
              You have nothing to lose. Try the entire system risk-free for 30
              days. If it's not for you, we'll give you every penny back.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 11 — EVERYTHING YOU GET TODAY */}
      <section className="fade-in py-24 px-6 bg-[#000000] border-t border-[#1a1a1a]" data-id="element-389">
        <div className="max-w-5xl mx-auto" data-id="element-390">
          <div className="text-center mb-16" data-id="element-391">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-392">
              Everything You Get <span className="text-[#D4AF37]" data-id="element-393">Today</span>
            </h2>
            <p className="text-lg text-[#999999]" data-id="element-394">
              Total Value: <span className="line-through" data-id="element-395">$997</span> — Your
              Price: <span className="text-[#D4AF37] font-bold" data-id="element-396">$17.99</span>
            </p>
          </div>

          <div className="space-y-4 mb-12" data-id="element-397">
            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-center justify-between" data-id="element-398">
              <div className="flex items-center gap-4" data-id="element-399">
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0" data-id="element-400" />
                <span className="text-base text-white" data-id="element-401">
                  Luxury Watch Business From Scratch (Step-by-Step System)
                </span>
              </div>
              <span className="text-[#999999] text-sm" data-id="element-402">Value: $197</span>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-center justify-between" data-id="element-403">
              <div className="flex items-center gap-4" data-id="element-404">
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0" data-id="element-405" />
                <span className="text-base text-white" data-id="element-406">
                  Negotiation & Pricing Templates
                </span>
              </div>
              <span className="text-[#999999] text-sm" data-id="element-407">Value: $97</span>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-center justify-between" data-id="element-408">
              <div className="flex items-center gap-4" data-id="element-409">
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0" data-id="element-410" />
                <span className="text-base text-white" data-id="element-411">
                  Watch Sourcing Guide
                </span>
              </div>
              <span className="text-[#999999] text-sm" data-id="element-412">Value: $147</span>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-center justify-between" data-id="element-413">
              <div className="flex items-center gap-4" data-id="element-414">
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0" data-id="element-415" />
                <span className="text-base text-white" data-id="element-416">
                  Sales Scripts (DM, Phone, Follow-Up)
                </span>
              </div>
              <span className="text-[#999999] text-sm" data-id="element-417">Value: $97</span>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-center justify-between" data-id="element-418">
              <div className="flex items-center gap-4" data-id="element-419">
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0" data-id="element-420" />
                <span className="text-base text-white" data-id="element-421">
                  Bonus: Buy Low, Sell High Strategies
                </span>
              </div>
              <span className="text-[#999999] text-sm" data-id="element-422">Value: $197</span>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-center justify-between" data-id="element-423">
              <div className="flex items-center gap-4" data-id="element-424">
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0" data-id="element-425" />
                <span className="text-base text-white" data-id="element-426">
                  7-Day Quick Start Action Plan
                </span>
              </div>
              <span className="text-[#999999] text-sm" data-id="element-427">Value: $97</span>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-center justify-between" data-id="element-428">
              <div className="flex items-center gap-4" data-id="element-429">
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0" data-id="element-430" />
                <span className="text-base text-white" data-id="element-431">
                  Lifetime Access & Updates
                </span>
              </div>
              <span className="text-[#999999] text-sm" data-id="element-432">Value: $97</span>
            </div>

            <div className="bg-[#0a0a0a] p-6 rounded-xl border border-[#1a1a1a] flex items-center justify-between" data-id="element-433">
              <div className="flex items-center gap-4" data-id="element-434">
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0" data-id="element-435" />
                <span className="text-base text-white" data-id="element-436">
                  30-Day Money-Back Guarantee
                </span>
              </div>
              <span className="text-[#999999] text-sm" data-id="element-437">Priceless</span>
            </div>
          </div>

          <div className="bg-[#D4AF37]/10 border-2 border-[#D4AF37] p-8 rounded-xl text-center" data-id="element-438">
            <p className="text-[#999999] text-sm mb-2" data-id="element-439">Total Value</p>
            <p className="text-4xl font-bold text-white line-through mb-2" data-id="element-440">
              $997
            </p>
            <p className="text-[#D4AF37] text-base font-semibold mb-4" data-id="element-441">
              Today's Price
            </p>
            <p className="text-6xl font-bold text-[#D4AF37] mb-6" data-id="element-442">$17.99</p>
            <p className="text-sm text-[#999999]" data-id="element-443">
              98% OFF — Limited Time Only
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 12 — PRICE ANCHORING */}
      <section className="fade-in py-24 px-6 bg-[#000000] border-t border-[#1a1a1a]" data-id="element-444">
        <div className="max-w-5xl mx-auto" data-id="element-445">
          <div className="text-center mb-12" data-id="element-446">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-447">
              This System Should Cost{' '}
              <span className="text-[#666666] line-through" data-id="element-448">$497</span>… But
              Today You Can Get It For Just{' '}
              <span className="text-[#D4AF37]" data-id="element-449">$17.99</span>
            </h2>
          </div>

          <div className="bg-[#0a0a0a] p-10 mb-10 rounded-xl border border-[#1a1a1a]" data-id="element-450">
            <div className="grid md:grid-cols-2 gap-5 mb-10" data-id="element-451">
              <div className="flex items-center gap-3" data-id="element-452">
                <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0" data-id="element-453" />
                <p className="text-sm text-[#cccccc]" data-id="element-454">
                  Full business blueprint
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-455">
                <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0" data-id="element-456" />
                <p className="text-sm text-[#cccccc]" data-id="element-457">Templates</p>
              </div>
              <div className="flex items-center gap-3" data-id="element-458">
                <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0" data-id="element-459" />
                <p className="text-sm text-[#cccccc]" data-id="element-460">Sourcing guide</p>
              </div>
              <div className="flex items-center gap-3" data-id="element-461">
                <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0" data-id="element-462" />
                <p className="text-sm text-[#cccccc]" data-id="element-463">Sales scripts</p>
              </div>
              <div className="flex items-center gap-3" data-id="element-464">
                <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0" data-id="element-465" />
                <p className="text-sm text-[#cccccc]" data-id="element-466">Bonus lessons</p>
              </div>
              <div className="flex items-center gap-3" data-id="element-467">
                <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0" data-id="element-468" />
                <p className="text-sm text-[#cccccc]" data-id="element-469">Lifetime access</p>
              </div>
            </div>

            <div className="text-center border-t border-[#1a1a1a] pt-8" data-id="element-470">
              <p className="text-[#999999] text-sm mb-3" data-id="element-471">Regular Price</p>
              <p className="text-4xl font-bold text-[#666666] line-through mb-3" data-id="element-472">
                $497
              </p>
              <p className="text-[#D4AF37] text-base font-semibold mb-3" data-id="element-473">
                Today Only
              </p>
              <p className="text-6xl font-bold text-[#D4AF37]" data-id="element-474">$17.99</p>
            </div>
          </div>

          <div className="text-center" data-id="element-475">
            <a href="https://pay.hotmart.com/R103200621U" target="_blank" rel="noopener noreferrer" className="inline-block px-12 py-5 bg-[#D4AF37] text-black font-semibold text-lg rounded-full hover:bg-[#C4A137] transition-all duration-200" data-id="element-476">
              Get Instant Access – $17.99
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 13 — FINAL CTA */}
      <section className="fade-in py-32 px-6 bg-[#000000] border-t border-[#1a1a1a]" data-id="element-477">
        <div className="max-w-4xl mx-auto text-center space-y-8" data-id="element-478">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-479">
            Don't Wait Another Year to{' '}
            <span className="text-[#D4AF37]" data-id="element-480">Start Your Watch Business</span>
          </h2>

          <p className="text-xl text-[#cccccc] leading-relaxed max-w-2xl mx-auto" data-id="element-481">
            Most beginners lose money not because they are bad, but because
            nobody explains how pricing really works.
          </p>

          <div className="bg-[#0a0a0a] p-8 rounded-xl border border-[#1a1a1a] max-w-2xl mx-auto" data-id="element-482">
            <div className="grid md:grid-cols-3 gap-6 mb-6" data-id="element-483">
              <div className="text-center" data-id="element-484">
                <Clock className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" data-id="element-485" />
                <p className="text-sm text-[#999999]" data-id="element-486">Instant Access</p>
              </div>
              <div className="text-center" data-id="element-487">
                <Shield className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" data-id="element-488" />
                <p className="text-sm text-[#999999]" data-id="element-489">30-Day Guarantee</p>
              </div>
              <div className="text-center" data-id="element-490">
                <Target className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" data-id="element-491" />
                <p className="text-sm text-[#999999]" data-id="element-492">Beginner-Friendly</p>
              </div>
            </div>
          </div>

          <p className="text-base text-[#D4AF37] font-medium" data-id="element-493">
            ⚠️ Launch pricing ends soon — Lock in $17.99 today
          </p>

          <a href="https://pay.hotmart.com/R103200621U" target="_blank" rel="noopener noreferrer" className="inline-block px-14 py-5 bg-[#D4AF37] text-black font-semibold text-xl rounded-full hover:bg-[#C4A137] transition-all duration-200" data-id="element-494">
            Get Instant Access – $17.99
          </a>

          <p className="text-sm text-[#666666]" data-id="element-495">
            Instant digital download • Start reading in 60 seconds
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[#000000] border-t border-[#1a1a1a]" data-id="element-496">
        <div className="max-w-4xl mx-auto text-center" data-id="element-497">
          <p className="text-[#666666] text-xs" data-id="element-498">
            © 2024 D&A TIMEPIECES. All rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}